function required(key: keyof ImportMetaEnv): string {
  const value = import.meta.env[key];
  if (!value) throw new Error(`Missing required environment variable: ${key}`);
  return value;
}

function optional(key: keyof ImportMetaEnv, fallback = ''): string {
  return (import.meta.env[key] as string | undefined) ?? fallback;
}

/**
 * A build-time feature flag. **FAIL-CLOSED: anything other than the exact string `"true"` is OFF**,
 * absence included.
 *
 * That direction is deliberate. The whole point of a gate is that a forgotten setting cannot leak
 * the feature to real users — the worst case here is a developer briefly missing a tab locally,
 * which is loud and harmless, versus an unreleased feature appearing in production, which is
 * neither. Every environment that WANTS the feature says so explicitly in its `.env` file.
 */
function flag(key: keyof ImportMetaEnv): boolean {
  return (import.meta.env[key] as string | undefined)?.trim() === 'true';
}

/**
 * The dev-auth shim lets you run the FE without Cognito. Set <c>VITE_DEV_AUTH_USER</c>
 * to a value matching the backend's <c>X-Dev-User</c> format (<c>email|username[|tz]</c>).
 * When set, the OIDC redirect is skipped and every API/SignalR call carries the dev header.
 * NEVER set this in production.
 */
export const devAuthUser: string | null =
  (import.meta.env.VITE_DEV_AUTH_USER as string | undefined)?.trim() || null;

export const devAuthEmail: string =
  devAuthUser ? (devAuthUser.split('|')[0] ?? 'dev') : '';

export const env = {
  apiBaseUrl: required('VITE_API_BASE_URL'),
  // Cognito values are only required when dev-auth is OFF.
  cognitoAuthority:     devAuthUser ? optional('VITE_COGNITO_AUTHORITY')     : required('VITE_COGNITO_AUTHORITY'),
  cognitoClientId:      devAuthUser ? optional('VITE_COGNITO_CLIENT_ID')     : required('VITE_COGNITO_CLIENT_ID'),
  cognitoRedirectUri:   devAuthUser ? optional('VITE_COGNITO_REDIRECT_URI')  : required('VITE_COGNITO_REDIRECT_URI'),
  cognitoPostLogoutUri: devAuthUser ? optional('VITE_COGNITO_POST_LOGOUT_URI'): required('VITE_COGNITO_POST_LOGOUT_URI'),
  cognitoHostedUi:      devAuthUser ? optional('VITE_COGNITO_HOSTED_UI')     : required('VITE_COGNITO_HOSTED_UI'),
  // Hotjar site id — set only in deployed dev/prod (.env.dev / .env.prod). Empty locally → Hotjar off.
  hotjarId:             optional('VITE_HOTJAR_ID'),
} as const;

/**
 * Backtesting AI — ON in dev, OFF in production (2026-08-17 user request).
 *
 * Gating happens in ONE place, `featureManifest`, because the router and the Sidebar both read that
 * list; a disabled entry therefore removes the rail item AND the route together, and a bookmarked
 * `/backtesting` falls through the router's `*` catch-all to the dashboard. The results route
 * (`/backtesting/:strategyId`) is NOT in the manifest and is gated separately in `router.tsx` — it
 * would otherwise stay reachable, which is exactly the hole a flag exists to close.
 *
 * It also hides the AI-CREDIT billing surfaces. Selling credits for a feature the buyer cannot open
 * is a refund waiting to happen, so the "Need more credits?" bar, the Buy-credits button and the
 * per-plan credit allowance all follow this same flag.
 */
export const featureBacktesting: boolean = flag('VITE_FEATURE_BACKTESTING');

/**
 * Plans / paid subscriptions — ON in dev, OFF in production (2026-08-17 user request:
 * "make sure that backtesting AI and plans are not enabled in prod").
 *
 * Off removes every route and entry point that could take someone toward a purchase: the
 * `/upgrade` pricing page, both Stripe return pages, the profile menu's plan row, and the
 * `ProUpsell` CTA. `SubscriptionGuard` also stops substituting the pricing page for the app, so a
 * caller the server considers unentitled still gets the product rather than a paywall they have no
 * way to clear.
 *
 * This is a CLIENT-side gate, deliberately independent of the backend's own `Stripe:Enabled`. The
 * two agree in production (both off) and neither depends on the other: with billing dark the BE
 * already returns `subscription: null`, which `useTier` and `isEntitled` both fail OPEN on, so the
 * app degrades to "everything available, nothing purchasable" from either direction.
 */
export const featurePlans: boolean = flag('VITE_FEATURE_PLANS');

/**
 * The Dashboard's chart widget — ON everywhere since 2026-10-24 (owner, 2026-10-23: ship the
 * TradingView chart and its data to Prod). It was OFF in production from 2026-09-10 (user request:
 * "the chart on the dashboard, make it a feature toggle" / "turn it off in production"). The same flag
 * mounts the chart snapshots (`PositionScreenshotGallery`) and the chart notes. The day's pictures
 * (`DayPictures`, the former `DayAttachmentsList`) no longer wait on it since 2026-10-29: a picture can be
 * added by hand, so the list shows wherever Plan My Day is on.
 *
 * Gating happens at the ONE mount point, `DashboardPage`, because `TradeChartWidget` has exactly one
 * caller. Not rendering it also stops its data: the widget's own `useClosedPositions` lookup and the
 * datafeed catalog/bars queries `TradeChart` fires never run, so a flagged-off chart costs no
 * requests rather than merely hiding a chart it still paid for.
 *
 * `TradeChart` and the `ReferenceChart` it wraps are untouched — the Backtester keeps its own chart
 * under `featureBacktesting`. This flag is only about the Dashboard's read-only copy.
 */
export const featureDashboardChart: boolean = flag('VITE_FEATURE_DASHBOARD_CHART');

/**
 * The Dashboard chart names only futures (2026-11-02, owner: "remove dukacopy data source from
 * production, only futures symbols") — ON in production, OFF in dev, which keeps the full Dukascopy list.
 *
 * On: the product picker lists the trader's own contracts and every futures market we chart
 * (`FUTURES_MARKETS`), named as futures, and the product button, the legend and the replay legend read
 * `futuresChartLabel` ("MESZ26", "NQ / MNQ"), never a datafeed name such as "US 500 Index". The candles
 * are still the stand-in series the futures map to (owner: "keep the stand-in, futures names only").
 *
 * The Dashboard's `TradeChart` reads it. The Backtester's chart and its composer do not: they are off in
 * production (`featureBacktesting`), and launching them there needs its own decision on this.
 */
export const featureChartFuturesOnly: boolean = flag('VITE_FEATURE_CHART_FUTURES_ONLY');

/**
 * Plan My Day (2026-09-09) — ON in dev, OFF in production until counsel signs off on the three AI
 * prompts (the BE's own `PlanMyDay:Enabled` is dark in Prod for the same reason).
 *
 * It gates every entry point the feature adds: the Topbar "Plan My Day" button, the Calendar day
 * panel's Plan / Review tabs and the Additional-notes card, the streak row, and the Start/End of
 * Trading Day + reminder-email block on Settings. Fail-closed like the other flags, so a forgotten
 * `.env` value hides an unfinished feature rather than shipping it.
 *
 * The Settings form still HYDRATES the trading-day values from `/me` when the flag is off — the PATCH
 * contract is wholesale, so echoing the server's own values back is the only way an unrendered field
 * cannot silently clear a saved time.
 */
export const featurePlanMyDay: boolean = flag('VITE_FEATURE_PLAN_MY_DAY');

/**
 * The AI side of Plan My Day (owner decision 2026-09-26: the switch is split so production can get the
 * manual journal only). ON in dev, OFF in production, pinned by `envFeatureFlags.test.ts`; the BE mirrors
 * it with `PlanMyDay:AiEnabled` (off in Prod, and behind the owner's second key there).
 *
 * It gates, on top of `featurePlanMyDay`: the Plan tab's `MANUAL / AI` control and AI chat (off = the manual
 * form only), the review tab's AI Assist card and dialog and its AI notice, the Dashboard's "Your day so far"
 * write-up card (owner: hidden while the AI is off), and the rewards (owner: "Rewards off with the AI"): the
 * streak cards' milestone line and the `How rewards work` link. The streaks themselves still count.
 */
export const featurePlanMyDayAi: boolean = featurePlanMyDay && flag('VITE_FEATURE_PLAN_MY_DAY_AI');

/**
 * The PAID day write-up (owner decision 2026-09-14) — ON in dev, OFF in production, and deliberately
 * SEPARATE from `featurePlanMyDay`: it changes how an already-shipped card is paid for, not whether
 * Plan My Day exists.
 *
 * It mirrors the backend's own `PlanMyDay:PaidDaySummaryEnabled` — but only the BACKEND flag is a true
 * kill switch. Turning THIS one off restores the D12 card exactly *when the server flag is off too*:
 * the GET auto-generates the recap, free, cached, stored nowhere, and `DaySummaryCard` prints it with
 * no button, no price, no credits query and no purchase request — the same one GET the card has always
 * made.
 *
 * ⚠️ **ROLLBACK ORDER: server flag OFF first, this bundle after.** With the server still selling and
 * this flag off, the GET returns no stored text (nothing was bought) while `DaySummaryCard` computes
 * `purchase = featurePaidDaySummary && data.purchaseEnabled` as false — so the card has neither text
 * nor an offer and renders NOTHING. The trader loses the recap entirely instead of getting the free
 * one back (`DaySummaryCard.test.tsx`, “shows nothing — not a button — when the SERVER still offers the
 * purchase”, pins that deliberately: a dev backend must never put a paid button in a dark build). Off
 * in the other order — or off at both ends — the free card is back exactly as it was.
 *
 * ON, `DaySummaryCard` shows the price and the button and NOTHING else until the write-up is bought
 * (the owner chose an empty card over a free teaser line); the stored text is then free to read
 * forever, and only an explicit, price-labelled press buys a fresher one.
 *
 * The card requires BOTH this flag and the server's own `purchaseEnabled` before it offers to charge:
 * pressing a button the server does not serve would be a 404, and printing a free recap beside a
 * "buy" button would be a lie about what the credit bought. Fail-closed like every flag here.
 */
export const featurePaidDaySummary: boolean = flag('VITE_FEATURE_PAID_DAY_SUMMARY');

/**
 * The economic calendar on the Calendar page (owner decision 2026-09-26): a small events banner on each
 * day cell of the month grid, an `Economic events` list in the day panel's Daily overview, and the same
 * list, compact, on the Plan my day tab. ON in dev and, since 2026-09-28, in production, mirroring the backend's
 * `EconomicCalendar:ShowInApp` (the read endpoint 404s where that is off). Fail-closed like every flag here.
 */
export const featureEconomicCalendar: boolean = flag('VITE_FEATURE_ECONOMIC_CALENDAR');

/**
 * The economic events' figures and details (owner decisions 2026-10-14): each event row of the Calendar
 * day panel becomes `caret · Time · Flag · Name · Previous · Forecast · Actual · Impact` (the flag replaces
 * the currency pill and the agency source label), and the caret opens a popover with the event's catalog
 * entry and a history chart from `GET /api/v1/economic-calendar/references/{id}`. ON in dev and, since
 * 2026-10-24, in production (OFF there from 2026-10-14, decision A4), pinned by `envFeatureFlags.test.ts`,
 * mirroring the backend's `EconomicCalendar:ShowFigures` (off: no new members on the events, the references endpoint
 * 404s). Off here, the card renders the 2026-09-28 row exactly (time, currency pill, name, source, Impact)
 * and asks for no details, whatever the backend sends. On top of `featureEconomicCalendar`; fail-closed.
 */
export const featureEconomicFigures: boolean = featureEconomicCalendar && flag('VITE_FEATURE_ECONOMIC_FIGURES');

/**
 * The AI features shown as "Coming soon" (owner, 2026-11-03: "develop the AI features as coming soon in
 * production", in three places: Plan My Day, Backtesting, the daily summary). ON in production, OFF in dev,
 * pinned by `envFeatureFlags.test.ts`. It never turns an AI feature on: each placeholder shows only where that
 * feature's own flag is OFF, says the feature's name and "Coming soon" and nothing else (the owner chose no
 * description), and calls no API, so it needs no AI chip, no AI notice and no consent. Off, every place is
 * exactly the build before it. Fail-closed like every flag here.
 */
export const featureAiComingSoon: boolean = flag('VITE_FEATURE_AI_COMING_SOON');

/**
 * Plan My Day's AI as "Coming soon": the journal on, its AI side off. The `Manual / AI` switch comes back on
 * the Plan and Review tabs and AI shows the Coming soon panel instead of the chat; the Dashboard shows the
 * `Your day so far` card as Coming soon. The rewards stay off with the AI.
 */
export const featurePlanMyDayAiComingSoon: boolean = featurePlanMyDay && !featurePlanMyDayAi && featureAiComingSoon;

/**
 * The AI Backtester as "Coming soon": the `Backtesting AI` rail item and `/backtesting` come back with a
 * Coming soon page that imports nothing from the backtester. A saved strategy's results route, the credit
 * surfaces and every backtester call stay off with `featureBacktesting`.
 */
export const featureBacktestingComingSoon: boolean = !featureBacktesting && featureAiComingSoon;
